import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { noteKeys, useCreateNote } from "@/lib/queries";
import { isTauri } from "@/lib/platform";

/**
 * Wires desktop-only native events into the app.
 *
 * Two events, both emitted from Rust:
 *
 * - `quick-capture` opens a fresh note in this window.
 * - `notes-changed` fires after the flying note files something. Without it the
 *   list here would keep showing stale data until the window happened to regain
 *   focus, which is exactly what does not happen while you are capturing from
 *   another app.
 *
 * The Tauri API is imported dynamically so the browser bundle never loads it.
 */
export function TauriBridge() {
  const navigate = useNavigate();
  const create = useCreateNote();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isTauri()) return;

    const unlisten: Array<() => void> = [];
    let cancelled = false;

    const bind = (event: string, handler: () => void) => {
      void import("@tauri-apps/api/event")
        .then(({ listen }) => listen(event, handler))
        .then((fn) => {
          if (cancelled) fn();
          else unlisten.push(fn);
        });
    };

    bind("quick-capture", () => {
      create.mutate({}, { onSuccess: (r) => navigate(`/notes/${r.note.id}`) });
    });
    bind("notes-changed", () => {
      void queryClient.invalidateQueries({ queryKey: noteKeys.all });
    });

    return () => {
      cancelled = true;
      for (const fn of unlisten) fn();
    };
  }, [create, navigate, queryClient]);

  return null;
}
